/* eslint-disable @typescript-eslint/no-explicit-any */
import { PROGRAM_IDS, UOC_PROGRAM_EVIDENCE_DEFINITION_IDS } from "@constants/programs";
import {
  IClassSubscription,
  IEvidence,
  INOSSelectCompetence,
  IUserProgramCertificate,
  ProgramCertificate,
  ProgramData,
  SubmissionState,
  UsageData,
} from "@interfaces/Interfaces";
import * as StringUtils from "@utils/stringUtils";

export function addStylesToHTML(html: string): string {
  const sanitizedHTML = addInlineAlignmentWrappers(html);

  return `
<body style="margin: 0; padding: 0;">
  ${sanitizedHTML}
</body>`;
}

function addInlineAlignmentWrappers(html: string): string {
  const baseStyleEnhancer = (style: string): string => {
    let enhanced = style.trim();

    if (enhanced && !enhanced.endsWith(";")) {
      enhanced = `${enhanced};`;
    }

    if (!/list-style-position/i.test(enhanced)) {
      enhanced += " list-style-position: inside;";
    }

    if (!/font-family/i.test(enhanced)) {
      enhanced += " font-family: Inter;";
    }

    return enhanced.trim();
  };

  const wrapContent = (align: "left" | "center" | "right", source: string): string => {
    const regex = new RegExp(
      `<li([^>]*)style="([^"]*text-align\\s*:\\s*${align}[^"]*)"([^>]*)>([\\s\\S]*?)<\\/li>`,
      "gi"
    );

    return source.replace(regex, (_match, before, style, after, content) => {
      const enhancedStyle = baseStyleEnhancer(style);
      const hasWrapper = /data-align-wrapper="true"/.test(content);
      const wrapperStyle = `text-align: ${align}; width: 100%; display: block;`;
      const wrappedContent = hasWrapper
        ? content
        : `<div data-align-wrapper="true" style="${wrapperStyle}">${content}</div>`;

      return `<li${before} style="${enhancedStyle}"${after}>${wrappedContent}</li>`;
    });
  };

  let updatedHTML = html;
  updatedHTML = wrapContent("left", updatedHTML);
  updatedHTML = wrapContent("center", updatedHTML);
  updatedHTML = wrapContent("right", updatedHTML);

  return updatedHTML;
}

export function hideUserRegions(programID: string): boolean {
  return [PROGRAM_IDS.NWAS_ARC, PROGRAM_IDS.SWAST_SP_AP_RP].includes(programID);
}

export function getUserRegions(regions: string[] | undefined): string {
  console.log("regions", regions);

  if (regions?.length) {
    return regions.join(", ");
  }

  return "None";
}

export function displayUserESRNumber(programID: string): boolean {
  return [PROGRAM_IDS.SWAST_PRECEPTORSHIP, PROGRAM_IDS.LAS_MTS, PROGRAM_IDS.LAS_CA].includes(programID);
}

export function getCertificateHeader(
  certificate: IUserProgramCertificate,
  programCertificate?: ProgramCertificate,
  program?: ProgramData
): string {
  if (program?.ID === PROGRAM_IDS.SWAST_HART_EPRR)
    if (programCertificate?.Description) {
      return programCertificate.Description + " certificate";
    }

  if (certificate.programData.competenceName) {
    return certificate.programData.competenceName + " certificate";
  }

  if (certificate.programData.certificateType) {
    return certificate.programData.certificateType;
  }

  return "Certificate";
}

export function getCompliancePercentage(id: string, usageData: UsageData[] | undefined): string {
  const usage = usageData?.find((item) => item.compId === id);
  const result = usage ? Number(usage.percentage) : 0;

  return `${Math.floor(result)}%`;
}

export function getEvidenceCountForStandard(id: string, allEvidence: IEvidence[]): number {
  let result = 0;

  const length = allEvidence.length;

  for (let i = 0; i < length; i++) {
    try {
      const evidence = allEvidence[i];
      const evidenceJSON = evidence.evidenceJSON;

      if (Array.from(evidenceJSON.Comps).some((item: any): item is INOSSelectCompetence => item.id === id)) {
        result += 1;
      }
    } catch (error) {
      console.log(error);
    }
  }

  return result;
}

export function getUserESRNumber(
  subscriptions: IClassSubscription[] | undefined,
  programSubscriptionIDs: string[] | undefined
): string {
  if (programSubscriptionIDs && subscriptions) {
    const programSubscription = subscriptions.find((item: IClassSubscription) => {
      const subscriptionPrograms: string[] = item.ParaFolioPrograms ?? [];

      return (
        subscriptionPrograms?.some((_sub) => programSubscriptionIDs.includes(_sub)) ||
        programSubscriptionIDs.includes(`${item.SubscriptionID}`)
      );
    });

    if (programSubscription?.ESR) {
      return programSubscription.ESR;
    }
  }

  return "N/A";
}

export function displayMentors(): boolean {
  return false;
}

export function getMentors(): string {
  return "None";
}

export function showApprovedByText(status: string | undefined): boolean {
  return (
    status === SubmissionState.Approved || status === SubmissionState.OnHold || status === SubmissionState.OnHoldReset
  );
}

export function getProgressChecksPageText(programID: string): string {
  if (programID === PROGRAM_IDS.UOC_APPRENTICESHIP) {
    return "Meetings and Feedback";
  }

  return "Progress Checks";
}

export function getProgressCheckStatusTextForPDF(
  programID: string | undefined,
  progressCheckID: string,
  status: string | undefined,
  evidence: IEvidence[]
): string {
  if (
    programID === PROGRAM_IDS.UOC_APPRENTICESHIP &&
    status === SubmissionState.Submitted &&
    evidence.some(
      (e) =>
        e.evidenceJSON?.programInfo?.progressCheckID === progressCheckID &&
        [
          UOC_PROGRAM_EVIDENCE_DEFINITION_IDS.LEVEL_5_END_OF_YEAR_REVIEW,
          UOC_PROGRAM_EVIDENCE_DEFINITION_IDS.LEVEL_6_END_OF_YEAR_REVIEW,
        ].includes(e.evidenceJSON?.programInfo?.programEvidenceDefinitionID) &&
        e.evidenceJSON?.programInfo?.approvalState === "Approved"
    )
  ) {
    return "Waiting for academic approval";
  }

  return StringUtils.getProgressCheckStatusText(status);
}
